import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { FormDialog } from "ui/form-dialog";
import AddToBatchSelectForm from "containers/admin/clients/client/approval-queue/components/add-to-batch-select-form/add-to-batch-select-form";
import { InfoMessage } from "ui/info-message";
import SelectedRemovableItems from "components/partials/selected-removable-items/selected-removable-items";
import type { TouchpointAttributes } from "hooks/api/use-touchpoints";

const SelectedTouchpointsModal = ({
  onCloseCallback,
  onConfirmCallback,
  isSomeTouchpointsSkipped,
  filteredByStatusList,
  isSubmitting = false,
  latestBatchId,
  isLatestBatchLoading = false,
  setBatchId,
  onRemoveSelectedItem,
  submitButtonLabel = "Add to batch",
}: {
  onCloseCallback: () => void;
  onConfirmCallback: () => void | Promise<void>;
  isSomeTouchpointsSkipped: boolean;
  filteredByStatusList: TouchpointAttributes[];
  isSubmitting?: boolean;
  latestBatchId?: string;
  isLatestBatchLoading?: boolean;
  setBatchId: (batchId: string, batchName?: string) => void;
  onRemoveSelectedItem: (itemId: string) => void;
  submitButtonLabel?: string;
}) => {
  const [isFormSubmitDisabled, setIsFormSubmitDisabled] = useState(true);
  const hasItemsToAdd = (filteredByStatusList?.length ?? 0) > 0;
  const isModalSubmitDisabled = isFormSubmitDisabled || !hasItemsToAdd || isSubmitting;
  const form = useForm<{ batchId: string }>({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: { batchId: "" },
  });

  useEffect(() => {
    if (!hasItemsToAdd) {
      onCloseCallback();
    }
  }, [hasItemsToAdd, onCloseCallback]);

  const handleConfirm = async () => {
    if (!hasItemsToAdd) return;
    await onConfirmCallback();
  };

  return (
    <FormDialog
      open
      onClose={onCloseCallback}
      headline="Add to batch"
      description="Please choose the batch where you would like to add the selected items."
      form={form}
      onSubmit={handleConfirm}
      submitDisabled={isModalSubmitDisabled}
      submitting={isSubmitting}
      submitButtonText={submitButtonLabel}
      submitLoadingMessage="Adding to batch...">
      <AddToBatchSelectForm
        onBatchIdChange={setBatchId}
        latestBatchId={latestBatchId}
        isLatestBatchLoading={isLatestBatchLoading}
        setIsSubmitDisabled={setIsFormSubmitDisabled}
      />

      <SelectedRemovableItems
        selectedList={filteredByStatusList}
        onRemoveSelectedItem={onRemoveSelectedItem}
        showStatus
        showBatch
      />

      {isSomeTouchpointsSkipped && (
        <InfoMessage color="warning">
          Some selected creative versions weren’t added because of their status. Only versions with the
          statuses “Not reviewed yet” or “In review” can be included in the approval batch.
        </InfoMessage>
      )}
    </FormDialog>
  );
};

export default SelectedTouchpointsModal;
